import { Response, apiAbortController, ChangeLog } from '@ui/src';
import api from '../../../../api/api';
import { NotificationTemplateTypes } from '../../../notification-settings/types';
import { Device, DeviceSession } from '../../../settings/profile/types';
import {
  UserDetails,
  CreateUserRequest,
  EditUserRequest,
  UserPermissions,
  EditPermissionsRequest,
  UserValidator,
} from '../../types';
import { Lock, LockAuditLog, LockFormValues } from '../../../security/types';

export const usersApi = api.injectEndpoints({
  endpoints: (build) => ({
    getUsers: build.query<Response<UserDetails>, string>({
      query: (query) => ({
        url: `/v1/employees?take=9999&${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getUser: build.query<{ data: UserDetails }, string>({
      query: (id) => ({
        url: `/v1/employees/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getUserDevices: build.query<
      Response<Device>,
      { id: string; cursor?: string }
    >({
      query: (data) => ({
        url: `/v1/employees/${data.id}/devices?take=20${
          data.cursor ? `&cursor=${data.cursor}` : ''
        }`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createUser: build.mutation<{ data: UserDetails }, CreateUserRequest>({
      query: (body) => ({
        url: '/v1/employees',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    editUser: build.mutation<
      { data: UserDetails },
      { body: EditUserRequest; id: string }
    >({
      query: (data) => ({
        url: `/v1/employees/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    editUserNotificationTypes: build.mutation<
      { data: UserDetails },
      {
        body: {
          notificationsTypes: NotificationTemplateTypes[];
        };
        id: string;
      }
    >({
      query: (data) => ({
        url: `/v1/employees/${data.id}/notifications-types`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    endUserSession: build.mutation<
      { data: { count: number } },
      {
        id: string;
        deviceId: string;
      }
    >({
      query: (data) => ({
        url: `/v1/employees/${data.id}/devices/${data.deviceId}/sessions`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getUserPermissions: build.query<{ data: UserPermissions }, string>({
      query: (id) => ({
        url: `/v1/employees/${id}/permissions`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    editPermissions: build.mutation<
      { data: UserPermissions },
      { body: EditPermissionsRequest; id: string }
    >({
      query: (data) => ({
        url: `/v1/employees/${data.id}/permissions`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getUserValidators: build.query<{ data: UserValidator }, void>({
      query: () => ({
        url: '/v1/employees/validators',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    disabledUser: build.mutation<{ data: UserDetails }, string>({
      query: (id) => ({
        url: `/v1/employees/${id}/disable`,
        method: 'PUT',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    enableUser: build.mutation<{ data: UserDetails }, string>({
      query: (id) => ({
        url: `/v1/employees/${id}/enable`,
        method: 'PUT',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    recoveryPassword: build.mutation<
      {
        data: {
          token: string;
          userId: string;
          expiresAt: string;
          clientId: string;
        };
      },
      {
        userId: string;
      }
    >({
      query: (body) => ({
        url: '/v1/recover',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getUserChangeLogs: build.query<Response<ChangeLog>, string>({
      query: (query) => ({
        url: `/v1/audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    resetNotificationTemplateToRole: build.mutation<
      { data: UserDetails },
      string
    >({
      query: (id) => ({
        url: `/v1/employees/${id}/notifications-types/reset-to-role`,
        method: 'PUT',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    resetNotificationTemplateToUserTemplate: build.mutation<
      { data: UserDetails },
      string
    >({
      query: (id) => ({
        url: `/v1/employees/${id}/notifications-types/reset`,
        method: 'PUT',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getUserActiveLocks: build.query<Response<Lock>, string>({
      query: (id) => ({
        url: `/v1/locks?take=1&locked=true&userId=${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getUserLocks: build.query<Response<Lock>, string>({
      query: (query) => ({
        url: `/v1/locks${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createUserLock: build.mutation<{ data: Lock }, LockFormValues>({
      query: (body) => ({
        url: '/v1/locks',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateUserLock: build.mutation<
      { data: Lock },
      { body: LockFormValues; id: string }
    >({
      query: (data) => ({
        url: `/v1/locks/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getUserLockItem: build.query<{ data: Lock }, string>({
      query: (id) => ({
        url: `/v1/locks/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getUserAuditLogs: build.query<Response<LockAuditLog>, string>({
      query: (query) => ({
        url: `/v1/idm-audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getUserLockItemAuditLogs: build.query<Response<LockAuditLog>, string>({
      query: (query) => ({
        url: `/v1/idm-audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getEmployeeDeviceSessions: build.query<
      Response<DeviceSession>,
      { id: string; entityId: string; query: string }
    >({
      query: (data) => ({
        url: `/v1/employees/${data.id}/devices/${data.entityId}/sessions${data.query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useLazyGetUsersQuery,
  useLazyGetUserQuery,
  useLazyGetUserPermissionsQuery,
  useGetUserValidatorsQuery,
  useLazyGetUserValidatorsQuery,
  useCreateUserMutation,
  useEditUserMutation,
  useEditPermissionsMutation,
  useEnableUserMutation,
  useDisabledUserMutation,
  useRecoveryPasswordMutation,
  useLazyGetUserChangeLogsQuery,
  useEditUserNotificationTypesMutation,
  useResetNotificationTemplateToRoleMutation,
  useResetNotificationTemplateToUserTemplateMutation,
  useLazyGetUserDevicesQuery,
  useEndUserSessionMutation,
  useLazyGetUserLocksQuery,
  useCreateUserLockMutation,
  useUpdateUserLockMutation,
  useLazyGetUserLockItemQuery,
  useLazyGetUserAuditLogsQuery,
  useLazyGetUserLockItemAuditLogsQuery,
  useLazyGetUserActiveLocksQuery,
  useLazyGetEmployeeDeviceSessionsQuery,
} = usersApi;

export const {
  endpoints: {
    getUsers,
    getUser,
    createUser,
    editUser,
    getUserPermissions,
    editPermissions,
    enableUser,
    disabledUser,
    recoveryPassword,
    editUserNotificationTypes,
    resetNotificationTemplateToRole,
    resetNotificationTemplateToUserTemplate,
    getUserDevices,
    endUserSession,
    getUserLocks,
    createUserLock,
    updateUserLock,
    getUserLockItem,
    getUserAuditLogs,
    getUserLockItemAuditLogs,
    getUserActiveLocks,
    getEmployeeDeviceSessions,
  },
} = usersApi;
